import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import {
  getUser,
  getUsers,
  patchUser,
  updateUser,
  updateUserAccessLevels,
} from "@/api";
import { getUserCountries } from "@/api/users";
import type { UserFilters } from "@/api/users";
import type { User } from "@/types/models";

export const usersQueryKeys = {
  useUser: (userId: User["id"] | undefined) => ["users", "getUser", userId],
  useUsers: () => ["users", "getUser"],
  useUserCountries: () => ["users", "countries"],
  useUsersPage: (
    page: number,
    searchQuery: string,
    locked: number | string,
    filters: UserFilters = {},
    sort?: string,
  ) => ["users", "getUser", page, searchQuery, locked, filters, sort],
};

export const useUsers = () => {
  const useUser = (userId: User["id"] | undefined) =>
    useQuery({
      queryFn: () => getUser(userId),
      queryKey: usersQueryKeys.useUser(userId),
      enabled: !!userId,
    });

  const useAllUsers = (
    page: number,
    searchQuery: string,
    locked: number | string,
    filters: UserFilters = {},
    sort?: string,
  ) =>
    useQuery({
      queryFn: () => getUsers(page, searchQuery, locked, filters, sort),
      queryKey: usersQueryKeys.useUsersPage(
        page,
        searchQuery,
        locked,
        filters,
        sort,
      ),
      placeholderData: keepPreviousData,
    });

  const useUserCountries = () =>
    useQuery({
      queryFn: getUserCountries,
      queryKey: usersQueryKeys.useUserCountries(),
      staleTime: 1000 * 60 * 60,
    });

  const useUpdateUser = () => useMutation({ mutationFn: updateUser });

  const usePatchUser = () => useMutation({ mutationFn: patchUser });

  const useUpdateUserAccessLevels = () =>
    useMutation({ mutationFn: updateUserAccessLevels });

  return {
    useUser,
    useUpdateUser,
    usePatchUser,
    useAllUsers,
    useUpdateUserAccessLevels,
    useUserCountries,
  };
};
